import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { mutationFor, queryFor, useApiConfig } from "@visioni/a3s-client-api/app";
import {
  listIncidentsRecomputesEndpoint,
  startIncidentsRecomputeEndpoint,
  type IncidentsRecomputeRun,
  type StartIncidentsRecomputeRequest,
  type StartIncidentsRecomputeResponse,
} from "../../client-messages/incidents-recompute";

import { useCursorPager, type CursorPager } from "./cursor-pager";

const startIncidentsRecomputeMutation = mutationFor(startIncidentsRecomputeEndpoint);
const listIncidentsRecomputesQuery = queryFor(listIncidentsRecomputesEndpoint);

/** Runs per page of the recompute history. The endpoint caps at 100. */
export const INCIDENTS_RECOMPUTES_PAGE_SIZE = 10;

/** How often the history refreshes while it waits for a run just started to appear. */
const INCIDENTS_RECOMPUTES_POLL_MS = 10_000;

/**
 * Starts recomputing genuine incidents over a period, resolving once the batch
 * has it, not when it is done. Support-only and behind `monitoring_incident_recompute`:
 * the API answers 403 to anyone else, and 501 where there are no incidents.
 */
export function useStartIncidentsRecompute() {
  const apiConfig = useApiConfig();
  const mutationConfig =
    apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };

  return useMutation<StartIncidentsRecomputeResponse, Error, StartIncidentsRecomputeRequest>({
    mutationFn: (request) => startIncidentsRecomputeMutation(mutationConfig, {}).mutationFn(request),
  });
}

export interface UseIncidentsRecomputesResult {
  readonly items: readonly IncidentsRecomputeRun[] | undefined;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
  /** When the page shown was fetched (epoch ms); absent before the first fetch. */
  readonly fetchedAt: number | undefined;
  readonly pager: CursorPager;
  /** The cursor for the next page, or `null` when there are no older runs. */
  readonly nextCursor: string | null;
}

/**
 * Every incidents recompute, newest first, paged like the backfills. Until
 * `pollUntil` (epoch ms), the page refreshes itself, so a run just started
 * appears once the batch records it.
 */
export function useIncidentsRecomputes({
  pollUntil,
}: { readonly pollUntil?: number | undefined } = {}): UseIncidentsRecomputesResult {
  const apiConfig = useApiConfig();
  const pager = useCursorPager("incidents-recomputes");

  const query = useQuery({
    ...listIncidentsRecomputesQuery(
      apiConfig,
      {},
      { limit: INCIDENTS_RECOMPUTES_PAGE_SIZE, ...(pager.cursor !== undefined && { cursor: pager.cursor }) },
    ),
    placeholderData: keepPreviousData,
    // Progress moves by the minute, and the app persists its query cache: re-read it on every mount.
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: () => (pollUntil !== undefined && Date.now() < pollUntil ? INCIDENTS_RECOMPUTES_POLL_MS : false),
  });

  return {
    items: query.data?.items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
    fetchedAt: query.dataUpdatedAt > 0 ? query.dataUpdatedAt : undefined,
    pager,
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}
